import { AppBar, Toolbar, Box, Typography, Button, IconButton, Badge, Container, Menu, MenuItem } from '@mui/material';
import { Link as RouterLink, NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useState } from 'react';
import ShoppingCartOutlinedIcon from '@mui/icons-material/ShoppingCartOutlined';
import AccountCircleOutlinedIcon from '@mui/icons-material/AccountCircleOutlined';
import { useAuth } from '../contexts/AuthContext';
import { useCart } from '../contexts/CartContext';
import { b2bColors } from '../styles/b2bTheme';

const NAV_LINKS = [
    { to: '/', label: 'Accueil', end: true },
    { to: '/catalogue', label: 'Catalogue' },
    { to: '/orders', label: 'Mes commandes' },
];

export default function Layout() {
    const { user, logout } = useAuth();
    const { totals } = useCart();
    const navigate = useNavigate();
    const [anchor, setAnchor] = useState(null);

    const handleLogout = async () => {
        setAnchor(null);
        await logout();
        navigate('/login');
    };

    return (
        <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
            <AppBar position="sticky">
                <Toolbar sx={{ gap: 2 }}>
                    <Typography
                        component={RouterLink}
                        to="/"
                        variant="h3"
                        sx={{ color: b2bColors.primary, textDecoration: 'none', fontWeight: 700, mr: 2 }}
                    >
                        Amabec <Box component="span" sx={{ color: b2bColors.textMuted, fontWeight: 500 }}>B2B</Box>
                    </Typography>

                    <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 0.5, flex: 1 }}>
                        {NAV_LINKS.map((link) => (
                            <NavLink
                                key={link.to}
                                to={link.to}
                                end={link.end}
                                style={({ isActive }) => ({
                                    padding: '8px 14px',
                                    borderRadius: 3,
                                    textDecoration: 'none',
                                    fontSize: 14,
                                    fontWeight: 500,
                                    color: isActive ? b2bColors.primary : b2bColors.ink,
                                    backgroundColor: isActive ? b2bColors.primaryLight : 'transparent',
                                })}
                            >
                                {link.label}
                            </NavLink>
                        ))}
                    </Box>

                    <Box sx={{ flex: { xs: 1, md: 0 } }} />

                    <IconButton
                        component={RouterLink}
                        to="/cart"
                        size="large"
                        aria-label={`Panier (${totals.count} articles)`}
                        sx={{ color: b2bColors.ink }}
                    >
                        <Badge badgeContent={totals.count} color="primary" max={99} aria-live="polite">
                            <ShoppingCartOutlinedIcon />
                        </Badge>
                    </IconButton>

                    <IconButton
                        size="large"
                        onClick={(e) => setAnchor(e.currentTarget)}
                        sx={{ color: b2bColors.ink }}
                        aria-label="Compte"
                    >
                        <AccountCircleOutlinedIcon />
                    </IconButton>
                    <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)}>
                        <MenuItem disabled sx={{ fontSize: 13, color: b2bColors.textMuted }}>
                            {user?.email}
                        </MenuItem>
                        <MenuItem onClick={handleLogout}>Se déconnecter</MenuItem>
                    </Menu>
                </Toolbar>

                {/* Mobile nav row — same links, below toolbar */}
                <Box sx={{ display: { xs: 'flex', md: 'none' }, gap: 0.5, px: 2, pb: 1, borderTop: `1px solid ${b2bColors.border}` }}>
                    {NAV_LINKS.map((link) => (
                        <NavLink
                            key={link.to}
                            to={link.to}
                            end={link.end}
                            style={({ isActive }) => ({
                                padding: '8px 10px',
                                borderRadius: 3,
                                textDecoration: 'none',
                                fontSize: 13,
                                fontWeight: 500,
                                color: isActive ? b2bColors.primary : b2bColors.ink,
                                backgroundColor: isActive ? b2bColors.primaryLight : 'transparent',
                            })}
                        >
                            {link.label}
                        </NavLink>
                    ))}
                </Box>
            </AppBar>

            <Container maxWidth="lg" sx={{ py: { xs: 2, md: 4 }, flex: 1 }}>
                <Outlet />
            </Container>

            <Box component="footer" sx={{ py: 2, textAlign: 'center', fontSize: 12, color: b2bColors.textMuted, borderTop: `1px solid ${b2bColors.border}` }}>
                © {new Date().getFullYear()} Amabec — Besoin d'aide? <Button size="small" href="mailto:b2b-orders@amabec.ca">b2b-orders@amabec.ca</Button>
            </Box>
        </Box>
    );
}
